import React, { useEffect, useState } from 'react';
import { Button, Modal, Form, Row, Col, Alert} from 'react-bootstrap';
import DataTable, { TableColumn } from 'react-data-table-component';
import { IconContext } from 'react-icons';
import {
  BsBellFill,
  BsBell,
  BsPencilSquare,
  BsTrash,
  BsPeopleFill,
  BsArrowLeftRight,
  BsTrash3Fill
} from 'react-icons/bs';

import usuariosServices from '../../Services/Catalogos/Usuarios.Services';
import Supervisor from "../Catalogos/CatEmpleados/DTO/Supervisor";

interface UsuarioShort {
  id: number;
  nombre: string;
  numEmpleado: string;
  usuario: string;
}

const ListarSupervisoresYMovimientos: React.FC = () => {
  const [supervisores, setSupervisores] = useState<Supervisor[]>([]);
  const [filtro, setFiltro] = useState('');

  // Edición / Nuevo
  const [showEditModal, setShowEditModal] = useState(false);
  const [formData, setFormData] = useState<Partial<Supervisor>>({
    idSupervisor: 0,
    nombre: '',
    correo: '',
    numEmpleado: '',
    departamento: '',
    idJefe: 0,
    notificarJefe: 0,
    esNominista: 0
  });

  // Usuarios asignados / compartidos
  const [showUsuariosModal, setShowUsuariosModal] = useState(false);
  const [usuarios, setUsuarios] = useState<UsuarioShort[]>([]);
  const [supervisorSeleccionado, setSupervisorSeleccionado] = useState<number | null>(null);
  const [mostrandoCompartidos, setMostrandoCompartidos] = useState(false);
  const [destinoId, setDestinoId] = useState(0);


  

  useEffect(() => {
    cargarSupervisores();
  }, []);

  const cargarSupervisores = async () => {
    try {
      const res = await usuariosServices.getAllSupervisor();
      setSupervisores(res.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const supervisoresFiltrados = supervisores.filter(s =>
    s.nombre.toLowerCase().includes(filtro.toLowerCase()) ||
    s.numEmpleado.toLowerCase().includes(filtro.toLowerCase()) ||
    (s.correo?.toLowerCase().includes(filtro.toLowerCase()) ?? false) ||
    (s.departamento?.toLowerCase().includes(filtro.toLowerCase()) ?? false)
  );

  const columns: TableColumn<Supervisor>[] = [
    { name: 'ID', selector: row => row.idSupervisor, width: '70px', center: true, sortable: true },
    { name: 'NumEmpleado', selector: row => row.numEmpleado, width: '130px', sortable: true },
    { name: 'Usuario', selector: row => row.correo || '', width: '200px', sortable: true },
    { name: 'Nombre', selector: row => row.nombre, width: '250px', sortable: true },
    { name: 'Correo', selector: row => row.correo || '', width: '250px', sortable: true },
    { name: 'Departamento', selector: row => row.departamento || '', width: '200px', sortable: true },
    {
      name: 'Notif.',
      width: '90px',
      center: true,
      cell: row => (
        <IconContext.Provider value={{ size: '20px' }}>
          {row.notificarJefe === 1 ? <BsBellFill color="orange" /> : <BsBell />}
        </IconContext.Provider>
      )
    },
    {
      name: 'Es Nominista',
      width: '120px',
      center: true,
      cell: row => (
        <div style={{ width: '100%' }}>
          {row.esNominista === 1 ? 'Si' : 'No'}
        </div>
      )
    },
    {
      name: 'Acciones',
      width: '120px',
      center: true,
      cell: row => (
        <div style={{ width: '100%' }} className="d-flex justify-content-center gap-2">
          <Button size="sm" variant="outline-primary" onClick={(e) => { e.stopPropagation(); abrirEditar(row); }}>
            <BsPencilSquare />
          </Button>
          <Button size="sm" variant="outline-danger" onClick={(e) => { e.stopPropagation(); eliminarSupervisor(row.numEmpleado); }}>
            <BsTrash />
          </Button>
        </div>
      )
    }
  ];

  const abrirNuevo = () => {
    setFormData({
      idSupervisor: 0,
      nombre: '',
      correo: '',
      numEmpleado: '',
      departamento: '',
      idJefe: 0,
      notificarJefe: 0,
      esNominista: 0
    });
    setShowEditModal(true);
  };

  const abrirEditar = (s: Supervisor) => {
    setFormData({ ...s });
    setShowEditModal(true);
  };

  const guardarSupervisor = async () => {
    if (!formData.nombre?.trim() || !formData.correo?.trim() || !formData.numEmpleado?.trim()) {
      alert('Nombre, correo y número de empleado son requeridos');
      return;
    }

    const data: Supervisor = {
      idSupervisor: formData.idSupervisor ?? 0,
      nombre: formData.nombre,
      correo: formData.correo,
      numEmpleado: formData.numEmpleado,
      departamento: formData.departamento ?? '',
      idJefe: formData.idJefe ?? 0,
      notificarJefe: formData.notificarJefe ?? 0,
      esNominista: formData.esNominista ?? 0
    };

    try {
      await usuariosServices.upsSupervisor(data);
      alert('Supervisor guardado correctamente');
      setShowEditModal(false);
      cargarSupervisores();
    } catch (e: any) {
      alert('Error: ' + e.message);
    }
  };

  const eliminarSupervisor = async (numEmpleado: string) => {
    if (!window.confirm('¿Estás seguro de eliminar este supervisor?')) return;
    const sup = supervisores.find(s => s.numEmpleado === numEmpleado);
    if (!sup) return;

    try {
      const res = await usuariosServices.getAllUsuarioPorSupervisor(sup.idSupervisor.toString());
      if (res.data.length > 0) {
        alert('Este supervisor tiene usuarios asignados. Reasígnalos primero.');
        return;
      }
      await usuariosServices.deleteSupervisor(numEmpleado);
      alert('Supervisor eliminado');
      cargarSupervisores();
    } catch (e: any) {
      alert('Error: ' + e.message);
    }
  };

  const abrirUsuarios = async (idSupervisor: number, compartidos: boolean = false) => {
    try {
      const res = compartidos
        ? await usuariosServices.getAllUsuarioPorSupervisorCompartido(idSupervisor.toString())
        : await usuariosServices.getAllUsuarioPorSupervisor(idSupervisor.toString());
      setUsuarios(res.data || []);
      setSupervisorSeleccionado(idSupervisor);
      setMostrandoCompartidos(compartidos);
      setShowUsuariosModal(true);
    } catch (e) {
      alert('Error al cargar usuarios');
    }
  };

  return (
    <>
      {/* Lista de Supervisores */}
      <Alert className="mb-3">
        <Row>
          <Col xs={3}><h5>Lista de Supervisores</h5></Col>
          <Col xs={6}>
            <Form.Control
              type="text"
              size="sm"
              placeholder="Buscar..."
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
            />
          </Col>
          <Col xs={3} className="text-end">
            <Button size="sm" onClick={abrirNuevo}>+ Nuevo Supervisor</Button>
          </Col>
        </Row>
      </Alert>

      <DataTable
        noHeader
        columns={columns}
        data={supervisoresFiltrados}
        pagination
        paginationPerPage={10}
        highlightOnHover
        pointerOnHover
        striped
        dense
        onRowClicked={(row) => abrirUsuarios(row.idSupervisor)}
      />

      {/* Modal Editar / Nuevo Supervisor */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{formData.idSupervisor ? 'Editar' : 'Nuevo'} Supervisor</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col md={6}><Form.Label>Nombre</Form.Label><Form.Control value={formData.nombre || ''} onChange={e => setFormData({...formData, nombre: e.target.value})} /></Col>
            <Col md={6}><Form.Label>Correo</Form.Label><Form.Control value={formData.correo || ''} onChange={e => setFormData({...formData, correo: e.target.value})} /></Col>
            <Col md={6}><Form.Label>Núm. Empleado</Form.Label><Form.Control value={formData.numEmpleado || ''} onChange={e => setFormData({...formData, numEmpleado: e.target.value})} /></Col>
            <Col md={6}><Form.Label>Departamento</Form.Label><Form.Control value={formData.departamento || ''} onChange={e => setFormData({...formData, departamento: e.target.value})} /></Col>
            <Col md={6}><Form.Label>ID Jefe</Form.Label><Form.Control type="number" value={formData.idJefe || 0} onChange={e => setFormData({...formData, idJefe: Number(e.target.value)})} /></Col>
            <Col md={3}><Form.Label>Notificar Jefe</Form.Label>
              <Form.Select value={formData.notificarJefe || 0} onChange={e => setFormData({...formData, notificarJefe: Number(e.target.value)})}>
                <option value={1}>Sí</option><option value={0}>No</option>
              </Form.Select>
            </Col>
            <Col md={3}><Form.Label>Es Nominista</Form.Label>
              <Form.Select value={formData.esNominista || 0} onChange={e => setFormData({...formData, esNominista: Number(e.target.value)})}>
                <option value={1}>Sí</option><option value={0}>No</option>
              </Form.Select>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>Cancelar</Button>
          <Button variant="primary" onClick={guardarSupervisor}>Guardar</Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Usuarios Asignados / Compartidos */}
      <Modal show={showUsuariosModal} onHide={() => setShowUsuariosModal(false)} size="lg" scrollable>
        <Modal.Header closeButton>
          <Modal.Title>
            {mostrandoCompartidos ? 'Usuarios Compartidos Permisos-Vacaciones' : 'Usuarios Asignados Permisos-Vacaciones'} (Supervisor ID: {supervisorSeleccionado})
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          <Alert className="mb-0 rounded-0">
            <Row>
              <Col xs={12}>
                <Form.Control
                  type="text"
                  size="sm"
                  placeholder="Buscar usuario..."
                  className="mb-2"
                />
              </Col>
            </Row>
          </Alert>
          <DataTable
            noHeader
            columns={[
              { name: 'Nombre', selector: row => row.nombre, width: '250px' },
              { name: 'NumEmpleado', selector: row => row.numEmpleado, width: '150px' },
              { name: 'Usuario', selector: row => row.usuario, width: '200px' },
              {
                name: 'Acción',
                width: '150px',
                center: true,
                cell: row => !mostrandoCompartidos ? (
                  <Button size="sm" variant="outline-primary" onClick={async () => {
                    if (destinoId === 0) { alert('Seleccione supervisor destino'); return; }
                    await usuariosServices.compartirUsuarioIndividual(row.numEmpleado, destinoId);
                    alert('Compartido');
                  }}>
                    Compartir
                  </Button>
                ) : (
                  <Button size="sm" variant="outline-danger" onClick={async () => {
                    if (!window.confirm('¿Quitar?')) return;
                    await usuariosServices.eliminarUsuarioCompartidoIndividual(row.numEmpleado, supervisorSeleccionado!);
                    abrirUsuarios(supervisorSeleccionado!, true);
                  }}>
                    Quitar
                  </Button>
                )
              }
            ]}
            data={usuarios}
            pagination
            paginationPerPage={10}
            striped
            dense
            noDataComponent="No hay usuarios"
          />

          <div className="p-3 border-top bg-light">
            <Row className="align-items-end g-3">
              <Col md={7}>
                <Form.Label>Supervisor destino</Form.Label>
                <Form.Select value={destinoId} onChange={e => setDestinoId(Number(e.target.value))}>
                  <option value={0}>-- Seleccione --</option>
                  {supervisores.map(s => (
                    <option
                                          key={s.idSupervisor}
                                          value={s.idSupervisor}
                                        >
                                          {s.idSupervisor} -{" "}
                                          {s.nombre} |{" "}
                                          {s.correo}
                                        </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={5}>
                <Button variant="warning" className="me-2" disabled={destinoId===0} onClick={async () => {
                  await usuariosServices.moverUsuariosSupervisor(supervisorSeleccionado!, destinoId);
                  alert('Movidos');
                  setShowUsuariosModal(false);
                }}>Mover</Button>
                <Button variant="success" disabled={destinoId===0} onClick={async () => {
                  await usuariosServices.compartirUsuariosConSupervisor(supervisorSeleccionado!, destinoId);
                  alert('Compartidos');
                  setShowUsuariosModal(false);
                }}>Compartir</Button>
              </Col>
            </Row>
          </div>
        </Modal.Body>
        <Modal.Footer>
          {mostrandoCompartidos && (
            <Button variant="link" className="text-danger p-0" onClick={async () => {
              if (!window.confirm('¿Limpiar TODOS los compartidos?')) return;
              await usuariosServices.eliminarUsuariosCompartidos(supervisorSeleccionado!);
              alert('Limpiados');
              setShowUsuariosModal(false);
            }}>
              <BsTrash3Fill className="me-1" /> Limpiar Todo
            </Button>
          )}
          <div className="ms-auto d-flex gap-2">
            <Button variant="outline-info" onClick={() => abrirUsuarios(supervisorSeleccionado!, !mostrandoCompartidos)}>
              {mostrandoCompartidos ? 'Ver Asignados' : 'Ver Compartidos'}
            </Button>
            <Button variant="secondary" onClick={() => setShowUsuariosModal(false)}>Cerrar</Button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ListarSupervisoresYMovimientos;